import { FileWithPath } from 'react-dropzone';
import 'whatwg-fetch';

import { maxFileSizeErrorText } from './utils/file-size-messages';

/** Matches Cloudinary unsigned upload preset limit for planner/Bizly uploads (40MB). */
export const CLOUDINARY_CLIENT_MAX_FILE_BYTES = 40 * 1024 * 1024;

const preset = import.meta.env.VITE_APP_CLOUDINARY_UPLOAD_PRESET;
const cloudName = import.meta.env.VITE_APP_CLOUDINARY_CLOUD_NAME;
const host = `https://api.cloudinary.com/v1_1/${cloudName}`;

export const getExtension = (path: string) => path.substr(path.lastIndexOf('.') + 1);
export const getFileName = (path: string) => path.substr(path.lastIndexOf('/') + 1);

const upload = (payload: FormData, useRaw: boolean) =>
    fetch(`${host}/${useRaw ? 'raw' : 'image'}/upload`, {
        method: 'POST',
        body: payload,
    })
        .then(res => res.json())
        .then(json => {
            if (json.error) {
                console.error(json.error);
                throw new Error(json.error.message);
            }
            return {
                cloudinaryId: json.public_id,
                title: json.original_filename,
                url: json.secure_url,
                format: getExtension(json.secure_url),
                resourceType: json.resource_type,
                bytes: json.bytes,
            };
        });

const createImagePayload = (file: File, isImage: boolean) => {
    const payload = new FormData();
    if (preset) {
        payload.append('upload_preset', preset);
    }
    payload.append('file', file);
    payload.append('resource_type', isImage ? 'image' : 'raw');

    return payload;
};

/** Get file extension from path or name (path is set by react-dropzone; plain File only has name) */
const getFileExtension = (file?: File | FileWithPath) =>
    getExtension((file as FileWithPath | undefined)?.path ?? file?.name ?? '');

export const uploadFile = (file?: File) => {
    if (!file) {
        return Promise.reject(new Error('uploadFile called without a file'));
    }

    const isImage = Boolean(getFileExtension(file).match(/(jpeg|jpg|gif|png)/));
    const payload = createImagePayload(file, isImage);

    return upload(payload, !isImage);
};

export const uploadCroppedImage = (blob: RequestInfo, imageTitle: string) => {
    return fetch(blob)
        .then(response => response.blob())
        .then(blob => {
            const file = new File([blob], imageTitle, { type: blob.type });
            const extension = (file: File) => getFileName(file.type);
            const isImage = Boolean(extension(file).match(/(jpeg|jpg|gif|png)/));
            const payload = createImagePayload(file, isImage);

            return upload(payload, !isImage);
        });
};

/** Maps common Cloudinary size errors to copy suitable for end users. */
export function getFriendlyCloudinaryUploadErrorMessage(
    error: unknown,
    maxBytes: number = CLOUDINARY_CLIENT_MAX_FILE_BYTES
): string | null {
    const message = error instanceof Error ? error.message : String(error);
    if (/too large|maximum is/i.test(message)) {
        return maxFileSizeErrorText(maxBytes);
    }
    return null;
}

export const uploadBizlyOSImages = async (file: File, propertyId: string, imageType: string) => {
    const preset = import.meta.env.VITE_APP_BIZLYOS_CLOUDINARY_UPLOAD_PRESET;
    const cloudName = import.meta.env.VITE_APP_BIZLYOS_CLOUDINARY_CLOUD_NAME;
    const host = `https://api.cloudinary.com/v1_1/${cloudName}`;

    const payload = new FormData();

    if (preset) {
        payload.append('upload_preset', preset);
    }
    payload.append('file', file);
    payload.append('resource_type', 'image');
    payload.append('asset_folder', `bizlyOS/properties/${propertyId}/${imageType}`);

    const response = await fetch(`${host}/image/upload`, {
        method: 'POST',
        body: payload,
    });

    const json = await response.json();

    if ('error' in json) {
        console.error(json.error);
        throw new Error(json.error.message);
    }

    return {
        ...json,
    };
};
